import { searchEntityRecords } from '@kizen/api/records';
import { type Filter, defFilter } from '@kizen/filters/filter';
import {
  getFilterTypeStepDataFromSave,
  loadSavedFilter,
} from '@kizen/filters/load';
import type { FilterMetadata, Next, StepData } from '@kizen/filters/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { fetchUrlConfigWithAxios } from 'ts-filters/utils';
import { KizenTypography } from '__app/typography';
import Button from '__components/Button';
import AxiosService from '__services/AxiosService';
import { useFilterVariables } from './hooks/useFilterVariables';
import { Filter as FilterComponent } from './Filter';
import {
  ClickableText,
  Container,
  ControlsWrapper,
  ControlsContainer,
  CustomObjectIdInput,
  Divider,
  FilterContainer,
  SaveControlsContainer,
  SavedItemContainer,
  SavedItems,
  SavedItemsContainer,
  SaveNameInput,
  SearchButton,
  StyledSwitch,
  XRemove,
} from './styled';

const LocalStoragePrefix = 'kzn_filters_testing__';

const AUTOMATION_VARS: [string, any][] = [['object_type', 'automation']];

const saveLocal = (name: string, value: any) => {
  localStorage.setItem(`${LocalStoragePrefix}${name}`, JSON.stringify(value));
};

const getLocal = (name: string) => {
  return JSON.parse(localStorage.getItem(`${LocalStoragePrefix}${name}`)!);
};

const removeLocal = (name: string) => {
  localStorage.removeItem(`${LocalStoragePrefix}${name}`);
};

const getAllSavedNames = () => {
  return Object.keys(localStorage).reduce<string[]>((acc, x) => {
    if (x.startsWith(LocalStoragePrefix)) {
      acc.push(x.split(LocalStoragePrefix).pop()!);
    }
    return acc;
  }, []);
};

type Ops = {
  build(): Record<string, any>;
  next: (
    current_step: string,
    next_steps: Next[],
    config?: FilterMetadata | undefined
  ) => void;
  save(): IterableIterator<[string, StepData]>;
  set(key: string, value: any): void;
  toString(): string;
  update(): void;
  isMyRecord(): boolean;
  isStageRecord(): boolean;
};

export const TestFixture = () => {
  const metadata = useSelector(selectFilterMetadataDefinition);
  const clientObjectId = useSelector(
    (state: any) => state.authentication.chosenBusiness.client_object.id
  );
  const [id, setId] = useState(clientObjectId);
  const [init, setInit] = useState<[string, any][]>([]);
  const [isAutomation, setIsAutomation] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savedItems, setSavedItems] = useState(getAllSavedNames());
  const [response, setResponse] = useState({});

  const { variables } = useFilterVariables({ objectId: id });
  const vars = isAutomation ? AUTOMATION_VARS : variables;
  const objectType = isAutomation
    ? 'automation'
    : variables.find((v) => v[0] === 'object_type')?.[1];

  const [filter, setFilter] = useState<Filter>(() => {
    return defFilter(metadata, { vars, init });
  });
  const [steps, setSteps] = useState<[string, StepData][]>([...filter]);

  const ops = useMemo<Ops>(() => {
    return {
      build: () => filter.build(),
      next: (...args: Parameters<Filter['nextSteps']>) => {
        filter.nextSteps(...args);
      },
      save: () => filter.save(),
      set: (...args: Parameters<Filter['set']>) => {
        filter.set(...args);
      },
      toString: () => filter.toString(),
      isMyRecord: () => filter.isMyRecord(),
      isStageRecord: () => filter.isStageRecord(),
      update: () => setSteps([...filter]),
    };
  }, [filter]);

  const handleSearch = useCallback(async () => {
    const filter = {
      and: null,
      field_ids: [],
      query: [
        {
          and: false,
          filters: [ops.build()],
          id: 'query-0',
        },
      ],
    };

    const res = await searchEntityRecords(AxiosService, id, { body: filter });

    setResponse(res);
  }, [id, ops]);

  const handleSave = () => {
    if (
      saveName?.length &&
      steps.every(([, { value }]) => value !== null) // TODO: handle multiselect default values of [] (or change to null)
    ) {
      saveLocal(saveName, [...ops.save()]);
      setSavedItems(getAllSavedNames());
      setSaveName('');
    }
  };

  const handleLoad = async (data: [string, any][]) => {
    const { vars = null } = data[0][1];
    const entries: typeof data = [
      getFilterTypeStepDataFromSave(data[0]),
      ...data.slice(1),
    ];
    const values = await loadSavedFilter(
      fetchUrlConfigWithAxios,
      metadata!,
      entries,
      vars
    );
    setInit(values);
  };

  useEffect(() => {
    const type = init?.find((v) => v[0] === 'filter_type')?.[1];
    const filter = defFilter(metadata, { vars, init, type });
    setFilter(filter);
    setSteps([...filter]);
  }, [vars, init, metadata]);

  return (
    <Container>
      <ControlsWrapper>
        <ControlsContainer>
          <CustomObjectIdInput
            value={id}
            label="Custom Object Id"
            onChange={(value: string) => {
              setInit([]);
              setId(value);
            }}
          />
          <StyledSwitch
            label="Automation"
            checked={isAutomation}
            onChange={(ev: any) => {
              setInit([]);
              setIsAutomation(ev.target.checked);
            }}
          />
          <SearchButton onClick={handleSearch}>Search</SearchButton>
        </ControlsContainer>
        <SaveControlsContainer>
          <SaveNameInput
            placeholder="Filter Name"
            value={saveName}
            onChange={setSaveName}
          />
          <Button noSpace onClick={handleSave}>
            Save
          </Button>
        </SaveControlsContainer>
      </ControlsWrapper>
      <SavedItemsContainer>
        <SavedItems>
          {savedItems.map((name) => (
            <SavedItemContainer key={name}>
              <ClickableText
                key={name}
                type="anchor"
                onClick={() => handleLoad(getLocal(name))}
              >
                {name}
              </ClickableText>
              <XRemove
                onClick={() => {
                  removeLocal(name);
                  setSavedItems(getAllSavedNames());
                }}
              >
                X
              </XRemove>
            </SavedItemContainer>
          ))}
        </SavedItems>
      </SavedItemsContainer>
      <Divider />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <KizenTypography as="span" weight="bold">
          Description:
        </KizenTypography>
        <KizenTypography as="span">{ops.toString()}</KizenTypography>
      </div>
      <FilterContainer>
        <FilterComponent
          steps={steps}
          objectType={objectType}
          set={ops.set}
          next={ops.next}
          onChange={ops.update}
        />
      </FilterContainer>
      <div style={{ display: 'flex', gap: '20px', marginTop: '40px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '75%' }}>
          <KizenTypography>Payload</KizenTypography>
          <textarea
            style={{ fontSize: '16px', height: '500px', marginTop: '5px' }}
            value={JSON.stringify(ops.build(), null, '\t')}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '75%' }}>
          <KizenTypography>Search Response</KizenTypography>
          <textarea
            style={{ fontSize: '16px', height: '500px', marginTop: '5px' }}
            value={JSON.stringify(response, null, '\t')}
          />
        </div>
      </div>
      <div style={{ marginTop: '40px' }}>
        <h4>Step Data</h4>
        <textarea
          value={JSON.stringify(
            steps.map(([k, { value }]) => [k, value]),
            null,
            '\t'
          )}
          style={{ fontSize: '16px', width: '100%', height: '300px' }}
        />
      </div>
      <div style={{ marginTop: '40px' }}>
        <h4>Step Data (full config)</h4>
        <textarea
          value={JSON.stringify(steps, null, '\t')}
          style={{ fontSize: '16px', width: '100%', height: '300px' }}
        />
      </div>
      <div style={{ marginTop: '40px' }}>
        <h4>Save Result</h4>
        <textarea
          value={JSON.stringify(Array.from(ops.save()), null, '\t')}
          style={{ fontSize: '16px', width: '100%', height: '300px' }}
        />
      </div>
    </Container>
  );
};
